/**
 * MHDF Momentum — Subscription Route Gate
 * 
 * Wraps protected routes that require an active subscription.
 * Redirects to /pricing if no subscription, shows banner if past_due.
 * 
 * Usage in App.js:
 *   <Route path="/dashboard" element={
 *     <SubscriptionGate>
 *       <ClientDashboard />
 *     </SubscriptionGate>
 *   } />
 * 
 * Or with plan restrictions:
 *   <Route path="/clinician-dashboard" element={
 *     <SubscriptionGate requiredPlans={['clinician_practitioner', 'clinician_practice', 'clinician_org']}>
 *       <ClinicianDashboard />
 *     </SubscriptionGate>
 *   } />
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import LoadingSpinner from './LoadingSpinner';

export default function SubscriptionGate({ children, requiredPlans = [] }) {
  const {
    loading,
    isActive,
    isAdmin,
    isLegacyPlan,
    isPastDue,
    plan,
    status,
    openPortal,
  } = useSubscription();

  // Loading state
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}>
        <LoadingSpinner />
      </div>
    );
  }

  // Admins always pass
  if (isAdmin) return <>{children}</>;

  // Past due — show warning banner but allow limited access
  if (isPastDue) {
    return (
      <>
        <div style={{
          background: '#FEF3CD',
          borderBottom: '1px solid #FFC107',
          padding: '12px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
        }}>
          <div>
            <strong style={{ color: '#856404' }}>⚠️ Payment Issue</strong>
            <span style={{ color: '#856404', marginLeft: '8px' }}>
              Your last payment failed. Please update your payment method to maintain access.
            </span>
          </div>
          <button
            onClick={openPortal}
            style={{
              background: '#856404',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
            }}
          >
            Update Payment
          </button>
        </div>
        {children}
      </>
    );
  }

  // No subscription yet (plan='none', status='none') — allow access with banner
  if (!isLegacyPlan && (plan === 'none' || status === 'none')) {
    return (
      <>
        <div style={{
          background: 'linear-gradient(135deg, #2D5A47 0%, #3A7C5F 100%)',
          borderBottom: '1px solid #2D5A47',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}>
          <div>
            <strong style={{ color: 'white', fontSize: '16px' }}>🎉 Welcome to MHDF Momentum!</strong>
            <p style={{ color: 'rgba(255, 255, 255, 0.9)', marginTop: '4px', fontSize: '14px' }}>
              Your account is active. Continue to the dashboard to begin your daily practice.
            </p>
          </div>
          <a
            href="/pricing"
            style={{
              background: 'white',
              color: '#2D5A47',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              textDecoration: 'none',
              display: 'inline-block',
            }}
          >
            View Plans
          </a>
        </div>
        {children}
      </>
    );
  }

  // No active subscription but not 'none' — canceled or expired — redirect to pricing
  if (!isActive && !isLegacyPlan) {
    return <Navigate to="/pricing" replace />;
  }

  // Check plan restrictions (if specified)
  if (requiredPlans.length > 0 && !requiredPlans.includes(plan)) {
    return (
      <div style={{
        maxWidth: '500px',
        margin: '80px auto',
        padding: '32px',
        textAlign: 'center',
        background: '#F8F9FA',
        borderRadius: '12px',
        border: '1px solid #E0E0E0',
      }}>
        <h2 style={{ marginBottom: '12px', color: '#1A1A1A' }}>Plan Upgrade Required</h2>
        <p style={{ color: '#666', marginBottom: '24px' }}>
          This feature requires a different plan. Your current plan is <strong>{plan}</strong>.
        </p>
        <a
          href="/pricing"
          style={{
            display: 'inline-block',
            background: '#2D5A47',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: '600',
          }}
        >
          View Plans
        </a>
      </div>
    );
  }

  // All checks passed — render the protected content
  return <>{children}</>;
}
